import React from 'react'

import classNames from 'classnames'

import type { PortableTextComponentProps } from '~/components/atoms/BlockContent/BlockContent'
import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import type { IconTypes } from '~/components/atoms/Icon/Icon'
import { Icon } from '~/components/atoms/Icon/Icon'
import Lozenge, { LozengeVariant } from '~/components/atoms/Lozenge/Lozenge'
import Text, { TextSizes } from '~/components/atoms/Text/Text'

import type { PartnerCategories } from '../PartnerCard/PartnerCard'

import styles from './DirectoryHeader.module.css'

interface DirectoryHeaderProps {
  title: string
  categories: PartnerCategories[]
  categoryClickHandler: (category: PartnerCategories) => void
  nearMeClickHandler: () => void
  locationClickHandler: () => void
  cancelLocationClickHandler: () => void
  nearMeActive: boolean
  selectedFilter?: string
  summary: PortableTextComponentProps['blocks'] | string
  selectedLocation: string | null
}

export const CategoryLozenge = ({
  category,
  icon,
  onClick,
  isActive,
}: {
  category: PartnerCategories | string
  icon?: IconTypes
  onClick?: () => void
  isActive: boolean
}) => {
  return (
    <div
      className={classNames(styles.category, isActive && styles.active)}
      onClick={onClick}
    >
      {icon && (
        <div className={styles.categoryIcon}>
          {' '}
          <Icon type={icon} />
        </div>
      )}
      <Text size={TextSizes.primaryTitleSml} className={styles.categoryName}>
        {typeof category === 'object' && category ? category.name : category}
      </Text>
    </div>
  )
}

const DirectoryHeader: React.FC<DirectoryHeaderProps> = ({
  title,
  categories,
  categoryClickHandler,
  nearMeClickHandler,
  locationClickHandler,
  cancelLocationClickHandler,
  nearMeActive,
  selectedFilter,
  summary,
  selectedLocation,
}) => {
  const categoriesOrdered = categories.sort((a, b) => {
    // Handle null values first
    if (a.categoryOrder === null && b.categoryOrder !== null) return 1
    if (a.categoryOrder !== null && b.categoryOrder === null) return -1
    // Sort by categoryOrder if both are not null
    return 1
  })

  return (
    <>
      <div className={styles.base}>
        <Container>
          <div className={styles.container}>
            <Text
              Tag="h2"
              size={TextSizes.primaryTitleLrger}
              uppercase
              className={styles.title}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: title,
                }}
              />
            </Text>
            {summary && (
              <div className={styles.mw}>
                {summary &&
                  (typeof summary === 'string' ? (
                    <Text size={TextSizes.bodyMed} Tag="p">
                      {summary}
                    </Text>
                  ) : (
                    <BlockContent
                      value={summary}
                      size={TextSizes.bodyLrg}
                    ></BlockContent>
                  ))}
              </div>
            )}
          </div>
        </Container>
      </div>
      <div className={styles.categoriesContainer}>
        <Container gutters={gutterTypes.never}>
          <div className={styles.categories}>
            <Lozenge
              title="Near me"
              onClick={nearMeClickHandler}
              isActive={nearMeActive}
              icon="GeoTarget"
              hideOnZero={!nearMeActive}
              count={nearMeActive ? `x` : ''}
              variant={LozengeVariant.directory}
            />
            <Lozenge
              title={selectedLocation || 'Location'}
              onClick={locationClickHandler}
              isActive={selectedLocation !== null}
              icon="GeoLocation"
              hideOnZero={true}
              count={selectedLocation !== null ? `x` : ''}
              variant={LozengeVariant.directory}
              iconClick={cancelLocationClickHandler}
            />

            <span className={styles.divider}></span>

            {categoriesOrdered?.length > 0 &&
              categoriesOrdered.map((category, idx) => {
                return (
                  <Lozenge
                    key={`category-${idx}`}
                    title={category.name}
                    onClick={() => {
                      categoryClickHandler(category)
                    }}
                    isActive={selectedFilter === category.slug}
                    hideOnZero={selectedFilter !== category.slug}
                    count={selectedFilter === category.slug ? `x` : ''}
                    variant={LozengeVariant.directory}
                  />
                )
              })}
          </div>
        </Container>
      </div>
    </>
  )
}

export default DirectoryHeader
